import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Client, getSubscriptionCategory } from '../../types';
import { ClientCheckTimestamp } from '../../services/api';
import StatusBadge from '../common/StatusBadge';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────

interface ClientsTableProps {
  clients: Client[];
  loading: boolean;
  checkTimestamps?: Record<string, ClientCheckTimestamp>;
}

type SortDirection = 'asc' | 'desc';

interface SortConfig {
  column: string;
  direction: SortDirection;
}

interface ColumnFilters {
  companyName?: string;
  status?: string;
  paymentStatus?: string;
  pax8Id?: string;
  tenantId?: string;
  lastSync?: string;
  lastEmailCheck?: string;
  lastSecurityScan?: string;
}

type FilterableColumn = keyof ColumnFilters;

// ── Normalize + License helpers (unchanged) ────────────────────────────────

function normalizeProductName(name: string): string {
  const aliases: Record<string, string> = {
    'sharepointstorage': 'office 365 extra file storage',
    'spb': 'microsoft 365 business premium',
    'enterprisepack': 'office 365 e3',
    'enterprisepremium': 'office 365 e5',
    'exchangestandard': 'exchange online plan 1',
    'exchangeenterprise': 'exchange online plan 2',
    'exchangearchive': 'exchange online archiving',
    'flow_free': 'microsoft power automate free',
    'powerapps_viral': 'power apps free',
    'power_bi_standard': 'power bi free',
    'teams_exploratory': 'microsoft teams exploratory',
    'mcoev': 'microsoft teams phone standard',
    'intune_a': 'microsoft intune',
    'ems': 'enterprise mobility security e3',
    'emspremium': 'enterprise mobility security e5',
  };
  let n = name
    .replace(/\s*\[New Commerce Experience\]/gi, '')
    .replace(/\s*\(NCE\)/gi, '')
    .replace(/\s*NCE$/gi, '')
    .trim()
    .toLowerCase();
  const aliasKey = n.replace(/[^a-z0-9]/g, '');
  if (aliases[aliasKey]) n = aliases[aliasKey];
  const rawKey = name.trim().toLowerCase();
  if (aliases[rawKey]) n = aliases[rawKey];
  return n;
}

function calculateLicenseIssues(client: Client): { severity: 'critical' | 'warning' | 'ok'; unusedCount: number; wastedAmount: number } {
  if (!client.subscriptions || client.subscriptions.length === 0) {
    return { severity: 'ok', unusedCount: 0, wastedAmount: 0 };
  }

  const activePaid = client.subscriptions.filter(
    s => s.status === 'active' && getSubscriptionCategory(s) === 'paid'
  );

  const products = new Map<string, { msTenantAssigned: number; pax8Qty: number; pax8UnitPrice: number }>();

  for (const sub of activePaid) {
    const normalized = normalizeProductName(sub.productName);
    const existing = products.get(normalized);
    if (existing) {
      if (sub.source === 'direct') {
        existing.msTenantAssigned += sub.assignedQuantity || 0;
      } else if (sub.source === 'pax8') {
        existing.pax8Qty += sub.quantity;
        if (sub.unitPrice) existing.pax8UnitPrice = sub.unitPrice;
      }
    } else {
      products.set(normalized, {
        msTenantAssigned: sub.source === 'direct' ? (sub.assignedQuantity || 0) : 0,
        pax8Qty: sub.source === 'pax8' ? sub.quantity : 0,
        pax8UnitPrice: sub.source === 'pax8' ? (sub.unitPrice || 0) : 0,
      });
    }
  }

  let totalUnused = 0;
  let totalWasted = 0;
  let hasCritical = false;
  let hasWarning = false;

  for (const [, product] of products) {
    if (product.pax8Qty <= 0) continue;
    const unused = product.pax8Qty - product.msTenantAssigned;
    if (unused > 0) {
      const utilization = product.pax8Qty > 0 ? (product.msTenantAssigned / product.pax8Qty) * 100 : 100;
      totalUnused += unused;
      totalWasted += unused * product.pax8UnitPrice;
      if (utilization < 25 || unused >= 5) {
        hasCritical = true;
      } else if (utilization < 50 && unused >= 3) {
        hasWarning = true;
      }
    }
  }

  return {
    severity: hasCritical ? 'critical' : hasWarning ? 'warning' : 'ok',
    unusedCount: totalUnused,
    wastedAmount: totalWasted,
  };
}

// ── Sort Icon ──────────────────────────────────────────────────────────────

function SortIcon({ direction, order }: { direction?: SortDirection; order?: number }) {
  if (!direction) {
    return (
      <span className="ml-1 text-muted-foreground text-xs select-none" title="Unsorted">
        ○
      </span>
    );
  }
  return (
    <span className="ml-1 inline-flex items-center text-blue-500 dark:text-blue-400 text-xs select-none font-bold">
      {direction === 'asc' ? '▲' : '▼'}
      {order !== undefined && order > 0 && (
        <span className="ml-0.5 text-[10px] bg-blue-500 text-white rounded-full w-3.5 h-3.5 inline-flex items-center justify-center leading-none">
          {order}
        </span>
      )}
    </span>
  );
}

// ── Filter Icon ────────────────────────────────────────────────────────────

function FilterIcon({ active }: { active: boolean }) {
  return (
    <svg
      className={`w-3 h-3 ml-1 ${active ? 'text-blue-500 dark:text-blue-400' : 'text-muted-foreground'}`}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
    </svg>
  );
}

// ── Filter Popup Portal ────────────────────────────────────────────────────

function FilterPopupPortal({ anchorRef, children, onClose }: {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  onClose: () => void;
}) {
  const popupRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 260)),
      });
    }
  }, [anchorRef]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(e.target as Node) &&
          anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose, anchorRef]);

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-card border border-border rounded-lg shadow-xl min-w-[220px] max-w-[280px]"
      style={{ top: pos.top, left: pos.left }}
    >
      {children}
    </div>,
    document.body
  );
}

// ── Text Filter Popup ──────────────────────────────────────────────────────

function TextFilterPopup({ value, suggestions, onChange, onClose }: {
  value: string;
  suggestions: string[];
  onChange: (v: string) => void;
  onClose: () => void;
}) {
  const [input, setInput] = useState(value);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    if (!input) return suggestions.slice(0, 15);
    const lower = input.toLowerCase();
    return suggestions.filter(s => s.toLowerCase().includes(lower)).slice(0, 15);
  }, [input, suggestions]);

  const apply = (val: string) => {
    onChange(val);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && highlightIdx < filtered.length) {
        apply(filtered[highlightIdx]);
      } else {
        apply(input);
      }
    }
  };

  return (
    <div className="p-3">
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={e => { setInput(e.target.value); setHighlightIdx(-1); }}
        onKeyDown={handleKeyDown}
        placeholder="Type to filter..."
        className="w-full px-2 py-1.5 text-sm border border-border rounded bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      {filtered.length > 0 && (
        <ul className="mt-2 max-h-40 overflow-y-auto text-sm">
          {filtered.map((s, i) => (
            <li
              key={s}
              className={`px-2 py-1 rounded cursor-pointer ${i === highlightIdx ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'hover:bg-accent text-foreground'}`}
              onMouseEnter={() => setHighlightIdx(i)}
              onClick={() => apply(s)}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex justify-between border-t border-border pt-2">
        <button
          onClick={() => { onChange(''); onClose(); }}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Clear
        </button>
        <button
          onClick={() => apply(input)}
          className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 font-medium"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ── Dropdown Filter Popup ──────────────────────────────────────────────────

function DropdownFilterPopup({ value, options, onChange, onClose }: {
  value: string;
  options: { label: string; value: string }[];
  onChange: (v: string) => void;
  onClose: () => void;
}) {
  const select = (v: string) => {
    onChange(v);
    onClose();
  };

  return (
    <div className="py-1">
      <button
        onClick={() => select('')}
        className={`w-full text-left px-3 py-1.5 text-sm ${!value ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-foreground hover:bg-accent'}`}
      >
        All
      </button>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => select(opt.value)}
          className={`w-full text-left px-3 py-1.5 text-sm ${value === opt.value ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-foreground hover:bg-accent'}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Column definitions ─────────────────────────────────────────────────────

interface ColumnDef {
  key: string;
  header: string;
  sortable?: boolean;
  filterable?: FilterableColumn;
  filterType?: 'text' | 'dropdown';
  filterOptions?: { label: string; value: string }[];
}

const COLUMN_DEFS: ColumnDef[] = [
  {
    key: 'companyName',
    header: 'Company Name',
    sortable: true,
    filterable: 'companyName',
    filterType: 'text',
  },
  { key: 'licenseIssues', header: 'Licenses' },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    filterable: 'status',
    filterType: 'dropdown',
    filterOptions: [
      { label: 'Active', value: 'active' },
      { label: 'At Risk', value: 'at-risk' },
      { label: 'Churned', value: 'churned' },
      { label: 'Pending Migration', value: 'pending-migration' },
    ],
  },
  {
    key: 'paymentStatus',
    header: 'Payment',
    sortable: true,
    filterable: 'paymentStatus',
    filterType: 'dropdown',
    filterOptions: [
      { label: 'Current', value: 'current' },
      { label: 'Flagged', value: 'flagged' },
      { label: 'Suspended', value: 'suspended' },
    ],
  },
  {
    key: 'pax8Id',
    header: 'Pax8 ID',
    sortable: true,
    filterable: 'pax8Id',
    filterType: 'dropdown',
    filterOptions: [
      { label: 'Has Pax8 ID', value: 'has' },
      { label: 'Missing Pax8 ID', value: 'missing' },
    ],
  },
  {
    key: 'tenantId',
    header: 'Tenant ID',
    sortable: true,
    filterable: 'tenantId',
    filterType: 'dropdown',
    filterOptions: [
      { label: 'Has Tenant ID', value: 'has' },
      { label: 'Missing Tenant ID', value: 'missing' },
    ],
  },
  {
    key: 'lastSync',
    header: 'Last Sync',
    sortable: true,
    filterable: 'lastSync',
    filterType: 'dropdown',
    filterOptions: [
      { label: 'Recent (<24h)', value: 'recent' },
      { label: 'Stale (>24h)', value: 'stale' },
      { label: 'Never', value: 'never' },
    ],
  },
  {
    key: 'lastEmailCheck',
    header: 'Email Health',
    sortable: true,
    filterable: 'lastEmailCheck',
    filterType: 'dropdown',
    filterOptions: [
      { label: 'Recent (<48h)', value: 'recent' },
      { label: 'Stale (>48h)', value: 'stale' },
      { label: 'Never', value: 'never' },
    ],
  },
  {
    key: 'lastSecurityScan',
    header: 'Security Scan',
    sortable: true,
    filterable: 'lastSecurityScan',
    filterType: 'dropdown',
    filterOptions: [
      { label: 'Recent (<4h)', value: 'recent' },
      { label: 'Stale (>4h)', value: 'stale' },
      { label: 'Never', value: 'never' },
    ],
  },
  {
    key: 'modifiedDate',
    header: 'Last Updated',
    sortable: true,
  },
];

// ── Main Component ─────────────────────────────────────────────────────────

// Timestamp filter helper
function matchesTimestampFilter(timestamp: string | null | undefined, filter: string, thresholdHours: number): boolean {
  if (filter === 'never') return !timestamp;
  if (!timestamp) return false;
  const age = Date.now() - new Date(timestamp).getTime();
  const thresholdMs = thresholdHours * 60 * 60 * 1000;
  if (filter === 'recent') return age < thresholdMs;
  if (filter === 'stale') return age >= thresholdMs;
  return true;
}

export default function ClientsTable({ clients, loading, checkTimestamps = {} }: ClientsTableProps) {
  const navigate = useNavigate();

  // Sort state — default: company name ascending
  const [sorts, setSorts] = useState<SortConfig[]>([{ column: 'companyName', direction: 'asc' }]);

  // Filter state
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});

  // Which filter popup is open
  const [openFilter, setOpenFilter] = useState<string | null>(null);

  // Refs for filter icon anchors
  const filterRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Pagination state (client-side)
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [columnFilters]);

  // ── Sort logic ─────────────────────────────────────────────────────────

  const handleSort = (column: string, addToMulti: boolean) => {
    setSorts(prev => {
      const existingIdx = prev.findIndex(s => s.column === column);

      if (addToMulti) {
        // Shift+click: add to or toggle within multi-sort
        if (existingIdx >= 0) {
          const existing = prev[existingIdx];
          if (existing.direction === 'asc') {
            const next = [...prev];
            next[existingIdx] = { column, direction: 'desc' };
            return next;
          } else {
            // Remove from multi-sort
            return prev.filter((_, i) => i !== existingIdx);
          }
        } else {
          return [...prev, { column, direction: 'asc' }];
        }
      } else {
        // Regular click: replace all sorts
        if (existingIdx >= 0 && prev.length === 1) {
          const existing = prev[existingIdx];
          if (existing.direction === 'asc') return [{ column, direction: 'desc' }];
          return []; // Remove sort (cycle: asc → desc → none)
        }
        return [{ column, direction: 'asc' }];
      }
    });
    setPage(1);
  };

  function getSortValue(client: Client, column: string): string {
    switch (column) {
      case 'companyName': return (client.companyName || '').trim().toLowerCase();
      case 'status': return client.status || '';
      case 'paymentStatus': return client.paymentStatus || '';
      case 'pax8Id': return client.pax8CompanyId ? '0' : '1';
      case 'tenantId': return client.tenantId ? '0' : '1';
      case 'lastSync': return checkTimestamps[client.id]?.lastSync || '';
      case 'lastEmailCheck': return checkTimestamps[client.id]?.lastEmailCheck || '';
      case 'lastSecurityScan': return checkTimestamps[client.id]?.lastSecurityScan || '';
      case 'modifiedDate': return client.modifiedDate || '';
      default: return '';
    }
  }

  // ── Filter + Sort + Paginate (computed every render for reliability) ───

  // 1. Filter
  const filtered = clients.filter(client => {
    if (columnFilters.companyName) {
      const filter = columnFilters.companyName.toLowerCase();
      if (!client.companyName.toLowerCase().includes(filter)) return false;
    }
    if (columnFilters.status && client.status !== columnFilters.status) return false;
    if (columnFilters.paymentStatus && client.paymentStatus !== columnFilters.paymentStatus) return false;
    if (columnFilters.pax8Id) {
      if (columnFilters.pax8Id === 'has' && !client.pax8CompanyId) return false;
      if (columnFilters.pax8Id === 'missing' && client.pax8CompanyId) return false;
    }
    if (columnFilters.tenantId) {
      if (columnFilters.tenantId === 'has' && !client.tenantId) return false;
      if (columnFilters.tenantId === 'missing' && client.tenantId) return false;
    }
    if (columnFilters.lastSync) {
      const ts = checkTimestamps[client.id]?.lastSync;
      if (!matchesTimestampFilter(ts, columnFilters.lastSync, 24)) return false;
    }
    if (columnFilters.lastEmailCheck) {
      const ts = checkTimestamps[client.id]?.lastEmailCheck;
      if (!matchesTimestampFilter(ts, columnFilters.lastEmailCheck, 48)) return false;
    }
    if (columnFilters.lastSecurityScan) {
      const ts = checkTimestamps[client.id]?.lastSecurityScan;
      if (!matchesTimestampFilter(ts, columnFilters.lastSecurityScan, 4)) return false;
    }
    return true;
  });

  // 2. Sort (multi-column) — always runs, no useMemo
  const processedClients = sorts.length > 0
    ? [...filtered].sort((a, b) => {
        for (const sort of sorts) {
          const aVal = getSortValue(a, sort.column);
          const bVal = getSortValue(b, sort.column);
          const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
          if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
        }
        return 0;
      })
    : filtered;

  // Pagination
  const totalFiltered = processedClients.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedClients = processedClients.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Company name suggestions for text filter
  const companyNames = useMemo(
    () => [...new Set(clients.map(c => c.companyName))].sort((a, b) => a.localeCompare(b)),
    [clients]
  );

  // Active filter count
  const activeFilterCount = Object.values(columnFilters).filter(Boolean).length;

  // ── Render helpers ─────────────────────────────────────────────────────

  const getSortInfo = (column: string) => {
    const idx = sorts.findIndex(s => s.column === column);
    if (idx < 0) return { direction: undefined, order: undefined };
    return { direction: sorts[idx].direction, order: sorts.length > 1 ? idx + 1 : undefined };
  };

  const setFilter = (key: FilterableColumn, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value || undefined }));
  };

  // ── Loading / Empty states ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="card overflow-hidden">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="card overflow-hidden">
        <div className="p-8 text-center text-muted-foreground">
          No clients found
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Active filter badge */}
      {activeFilterCount > 0 && (
        <div className="mb-2 flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            {totalFiltered} of {clients.length} clients shown
          </span>
          <button
            onClick={() => setColumnFilters({})}
            className="text-blue-600 dark:text-blue-400 hover:underline text-xs font-medium"
          >
            Clear all filters
          </button>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="bg-muted">
              <tr>
                {COLUMN_DEFS.map(col => {
                  const sortInfo = getSortInfo(col.key);
                  const filterActive = col.filterable ? !!columnFilters[col.filterable] : false;

                  return (
                    <th
                      key={col.key}
                      className={`${col.sortable ? 'cursor-pointer select-none hover:bg-accent' : ''}`}
                    >
                      <div className="flex items-center">
                        {/* Clickable header text + sort icon */}
                        <span
                          className="flex items-center"
                          onClick={col.sortable ? (e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleSort(col.key, e.shiftKey);
                          } : undefined}
                        >
                          {col.header}
                          {col.sortable && (
                            <SortIcon direction={sortInfo.direction} order={sortInfo.order} />
                          )}
                        </span>

                        {/* Filter icon */}
                        {col.filterable && (
                          <button
                            ref={el => { filterRefs.current[col.key] = el; }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenFilter(prev => prev === col.key ? null : col.key);
                            }}
                            className="ml-1 p-0.5 rounded hover:bg-accent"
                            title={`Filter by ${col.header}`}
                          >
                            <FilterIcon active={filterActive} />
                          </button>
                        )}
                      </div>

                      {/* Filter popup */}
                      {openFilter === col.key && col.filterable && (
                        <FilterPopupPortal
                          anchorRef={{ current: filterRefs.current[col.key] ?? null }}
                          onClose={() => setOpenFilter(null)}
                        >
                          {col.filterType === 'text' ? (
                            <TextFilterPopup
                              value={columnFilters[col.filterable] || ''}
                              suggestions={companyNames}
                              onChange={v => setFilter(col.filterable!, v)}
                              onClose={() => setOpenFilter(null)}
                            />
                          ) : (
                            <DropdownFilterPopup
                              value={columnFilters[col.filterable] || ''}
                              options={col.filterOptions || []}
                              onChange={v => setFilter(col.filterable!, v)}
                              onClose={() => setOpenFilter(null)}
                            />
                          )}
                        </FilterPopupPortal>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {pagedClients.map(client => (
                <tr
                  key={client.id}
                  onClick={() => navigate(`/clients/${client.id}`)}
                  className="cursor-pointer"
                >
                  {/* Company Name */}
                  <td>
                    <p className="font-medium text-foreground">
                      {client.companyName}
                    </p>
                  </td>

                  {/* Licenses */}
                  <td>
                    <LicenseCell client={client} />
                  </td>

                  {/* Status */}
                  <td>
                    <StatusBadge status={client.status} type="client" />
                  </td>

                  {/* Payment */}
                  <td>
                    <div>
                      <StatusBadge status={client.paymentStatus} type="payment" />
                      {client.paymentFlaggedDate && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Flagged: {format(parseISO(client.paymentFlaggedDate), 'MMM d, yyyy')}
                        </p>
                      )}
                    </div>
                  </td>

                  {/* Pax8 ID */}
                  <td>
                    {client.pax8CompanyId ? (
                      <span className="text-sm text-muted-foreground">
                        {client.pax8CompanyId.substring(0, 8)}...
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-sm text-red-500 dark:text-red-400 font-medium">
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        Missing
                      </span>
                    )}
                  </td>

                  {/* Tenant ID — red highlight when missing */}
                  <td>
                    {client.tenantId ? (
                      <span className="text-sm text-muted-foreground">
                        {client.tenantId.substring(0, 8)}...
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-sm text-red-500 dark:text-red-400 font-medium">
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        Missing
                      </span>
                    )}
                  </td>

                  {/* Last Sync */}
                  <td>
                    <TimestampCell timestamp={checkTimestamps[client.id]?.lastSync} staleHours={24} />
                  </td>

                  {/* Email Health */}
                  <td>
                    <TimestampCell timestamp={checkTimestamps[client.id]?.lastEmailCheck} staleHours={48} />
                  </td>

                  {/* Security Scan */}
                  <td>
                    <TimestampCell timestamp={checkTimestamps[client.id]?.lastSecurityScan} staleHours={4} />
                  </td>

                  {/* Last Updated */}
                  <td>
                    <span className="text-sm text-muted-foreground">
                      {format(parseISO(client.modifiedDate), 'MMM d, yyyy')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-card px-4 py-3 flex items-center justify-between border-t border-border sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="btn-secondary">Previous</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="btn-secondary">Next</button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Showing{' '}
                <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>{' '}
                to{' '}
                <span className="font-medium">{Math.min(currentPage * PAGE_SIZE, totalFiltered)}</span>{' '}
                of <span className="font-medium">{totalFiltered}</span> results
              </p>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-border bg-card text-sm font-medium text-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Hint */}
      <p className="mt-2 text-xs text-muted-foreground text-center">
        Click column to sort &middot; Shift+click for multi-sort
      </p>
    </div>
  );
}

// ── Timestamp Cell ──────────────────────────────────────────────────────────

function TimestampCell({ timestamp, staleHours }: { timestamp: string | null | undefined; staleHours: number }) {
  if (!timestamp) {
    return <span className="text-sm text-muted-foreground">Never</span>;
  }
  const age = Date.now() - new Date(timestamp).getTime();
  const isStale = age >= staleHours * 60 * 60 * 1000;
  const relative = formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  return (
    <span
      className={`text-sm ${isStale ? 'text-orange-500 dark:text-orange-400' : 'text-muted-foreground'}`}
      title={format(parseISO(timestamp), 'MMM d, yyyy h:mm a')}
    >
      {relative}
    </span>
  );
}

// ── License Cell (extracted for readability) ───────────────────────────────

function LicenseCell({ client }: { client: Client }) {
  const issues = calculateLicenseIssues(client);
  if (issues.severity === 'ok') {
    return (
      <span className="flex items-center text-green-600 dark:text-green-400 text-sm">
        <CheckCircle className="h-4 w-4 mr-1" />
        OK
      </span>
    );
  }
  if (issues.severity === 'critical') {
    return (
      <span className="flex items-center text-red-600 dark:text-red-400 text-sm font-medium" title={`${issues.unusedCount} unused Pax8 licenses (~$${issues.wastedAmount.toFixed(0)}/mo wasted)`}>
        <AlertCircle className="h-4 w-4 mr-1" />
        {issues.unusedCount} wasted
      </span>
    );
  }
  return (
    <span className="flex items-center text-orange-600 dark:text-orange-400 text-sm" title={`${issues.unusedCount} unused Pax8 licenses`}>
      <AlertTriangle className="h-4 w-4 mr-1" />
      {issues.unusedCount} unused
    </span>
  );
}
